import { AfterViewInit, Component, DestroyRef, HostListener, OnInit, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { FeedbackFormDialogService } from '@page/main/components/feedback-form/feedback-form-dialog.config';
import { MatDialog } from '@angular/material/dialog';
import { AnonymousFeedbackComponent } from '@components/anonymous-feedback/anonymous-feedback.component';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { DateAdapter } from '@angular/material/core';
import { HttpClient } from '@angular/common/http';
import { BuildUpdateDialogComponent } from '@components/build-update-dialog/build-update-dialog.component';
import { MaintenanceDialogComponent } from '@components/maintenance-dialog/maintenance-dialog.component';
import { AuthService } from '@auth/services/auth.service';
import { UserStore } from '@auth/services/user.store';
import { LanguageService } from '@services/language.service';
import { ru, kk, enGB } from 'date-fns/locale';
import { isTestEnvironment } from '@api-config';

const PRODUCTION_HOST = 'migration.enbek.kz';
const TEST_SITE_NOTICE_KEY = 'testSiteNoticeShown';

// Техникалық жұмыстар туралы хабарлама осы уақытқа дейін көрсетіледі (Астана уақыты)
const MAINTENANCE_END = new Date('2026-10-23T08:00:00+05:00');
const MAINTENANCE_NOTICE_KEY = 'maintenanceNoticeShown';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterModule, TranslateModule],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly translateService = inject(TranslateService);
  public readonly ntf = inject(NotificationService);
  private readonly matDialog = inject(MatDialog);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly _adapter = inject<DateAdapter<unknown>>(DateAdapter);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly userStore = inject(UserStore);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly feedbackFormDialog = inject(FeedbackFormDialogService);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.feedbackFormDialog.openIfRequested());

    effect(() => {
      if (!this.authService.isAuthenticated()) return;
      untracked(() => {
        if (this.userStore.user()) return;
        this.ntf.showLoader();
        this.userStore
          .ensureSession$()
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.ntf.hideLoader())
          )
          .subscribe(() => {
            if (this.userStore.user() && this.userStore.requiresRoleSelection()) {
              this.router.navigate(['/role-selection']);
            }
          });
      });
    });
  }

  ngOnInit(): void {
    // Сам сервис решает, включать ли счётчики: на localhost и dev/test стендах он ничего не подключает
    this.googleAnalyticsService.initialize();

    // localStorage-та белгісіз код (мысалы 'kk') болса, LanguageService оны әдепкі тілге ауыстырады —
    // әйтпесе translate жоқ /assets/i18n/kk.json файлын сұрайды
    this.setLang(this.languageService.currentLang());

    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.setLang(event.lang);
    });

    this.showTestSiteNotice();
  }

  /**
   * Тест стендте (localhost, migdev, migtest) сайтқа кіргенде «бұл тестілік сайт» деген ескерту.
   * Сессияда бір рет — беттер арасында ауысқанда қайта шықпайды.
   */
  private showTestSiteNotice(): void {
    if (!isTestEnvironment()) return;
    try {
      if (sessionStorage.getItem(TEST_SITE_NOTICE_KEY)) return;
      sessionStorage.setItem(TEST_SITE_NOTICE_KEY, '1');
    } catch {
      // sessionStorage қолжетімсіз (приват режим) — әр жүктелгенде көрсетеміз
    }

    // instant емес: бастапқы жүктелуде аударма файлы әлі келмеген болуы мүмкін
    this.translateService
      .get('testSiteNotice.message')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message: string) =>
        this.ntf.showMessageModal(message, {
          title: 'testSiteNotice.title',
          icon: 'warning_amber',
          link: { url: `https://${PRODUCTION_HOST}`, label: PRODUCTION_HOST },
        })
      );
  }

  private setLang(lang: string): void {
    this.translateService.use(lang);

    const dateLangMap: Record<string, any> = {
      kz: kk,
      ru: ru,
      en: enGB,
      ql: kk,
    };

    const dateLocale = dateLangMap[lang] || enGB;
    this._adapter.setLocale(dateLocale);
  }

  ngAfterViewInit(): void {
    this.checkBuildVersion();
    this.openMaintenanceDialog();
  }

  /** Жоспарлы техникалық жұмыстар туралы ескерту — аяқталғанға дейін, сессияда бір рет. */
  private openMaintenanceDialog(): void {
    if (new Date() >= MAINTENANCE_END) return;
    try {
      if (sessionStorage.getItem(MAINTENANCE_NOTICE_KEY)) return;
      sessionStorage.setItem(MAINTENANCE_NOTICE_KEY, '1');
    } catch {
      // sessionStorage қолжетімсіз (приват режим) — әр жүктелгенде көрсетеміз
    }

    this.matDialog.open(MaintenanceDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'maintenance-dialog-panel',
      backdropClass: 'custom-backdrop',
    });
  }

  private openUpdateDialog(): void {
    this.matDialog.open(BuildUpdateDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'update-warning-panel',
    });
  }

  private checkBuildVersion(): void {
    const localVersion = localStorage.getItem('buildVersion');
    this.http.get<{ buildVersion: string }>('/main-api/public/build-version').subscribe(({ buildVersion }) => {
      if (localVersion === buildVersion) return;
      if (localVersion) this.openUpdateDialog();
      localStorage.setItem('buildVersion', buildVersion);
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const mark = this.markContent();
    if (event.ctrlKey && event.key === 'Enter' && mark) {
      if (!this.authService.isAuthenticated()) {
        this.ntf.showNotification('snackbar-danger', this.translateService.instant('feedbackAuthRequired'));
        return;
      }
      this.matDialog.open(AnonymousFeedbackComponent, {
        width: '35vw',
        data: mark,
      });
    }
  }

  private markContent(): string {
    const selection = window.getSelection();
    const textSeleted = selection?.toString();
    return textSeleted || '';
  }
}
